import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, UnauthorizedError } from "@/api/client";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { X, Search, Trash2, Plus, MessageSquareText, Pencil, Check } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  currentThreadId: string;
  onSelect: (threadId: string) => void;
  onNew: () => void;
  onDeletedCurrent: () => void;
}

export default function HistoryDrawer({
  open,
  onClose,
  currentThreadId,
  onSelect,
  onNew,
  onDeletedCurrent,
}: Props) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const list = useQuery({
    queryKey: ["history"],
    queryFn: api.history,
    enabled: open,
    staleTime: 0,
  });
  const search = useQuery({
    queryKey: ["history-search", q],
    queryFn: () => api.historySearch(q),
    enabled: open && q.trim().length >= 2,
    staleTime: 0,
  });

  const threads = list.data?.items ?? [];
  const matched =
    q.trim().length >= 2
      ? new Set((search.data?.items ?? []).map((i) => i.thread_id))
      : null;
  const items = matched ? threads.filter((t) => matched.has(t.thread_id)) : threads;

  const onDelete = async (e: MouseEvent, threadId: string) => {
    e.stopPropagation();
    if (!window.confirm("이 대화를 삭제할까요?")) return;
    try {
      await api.historyDelete(threadId);
      qc.invalidateQueries({ queryKey: ["history"] });
      if (threadId === currentThreadId) onDeletedCurrent();
      toast("삭제했습니다.", "success");
    } catch (err) {
      toast(err instanceof UnauthorizedError ? "로그인이 필요합니다." : "삭제 실패", "error");
    }
  };

  const startEdit = (e: MouseEvent, threadId: string, title: string) => {
    e.stopPropagation();
    setEditingId(threadId);
    setEditTitle(title);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const saveEdit = async (threadId: string) => {
    if (saving) return;
    const next = editTitle.trim();
    setSaving(true);
    try {
      await api.historyRename(threadId, next);
      await qc.invalidateQueries({ queryKey: ["history"] });
      toast(next ? "제목을 수정했습니다." : "원래 제목으로 되돌렸습니다.", "success");
      cancelEdit();
    } catch (err) {
      toast(err instanceof UnauthorizedError ? "로그인이 필요합니다." : "수정 실패", "error");
    } finally {
      setSaving(false);
    }
  };

  const onEditKey = (e: KeyboardEvent<HTMLInputElement>, threadId: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit(threadId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose} aria-hidden />}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-80 max-w-[85vw] flex-col border-r border-border bg-background shadow-xl transition-transform",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5 pt-[env(safe-area-inset-top)]">
          <span className="font-semibold">대화 이력</span>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground active:bg-muted" aria-label="닫기">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-2 p-3">
          <button
            onClick={() => {
              onNew();
              onClose();
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0E1E2D] hover:bg-[#1C3954] py-2 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" /> 새 대화
          </button>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="대화 검색(2자 이상)"
              className="w-full rounded-lg border border-border bg-card py-2 pl-8 pr-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          {list.isLoading ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">불러오는 중…</p>
          ) : items.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">
              {q.trim().length >= 2 ? "검색 결과가 없습니다." : "대화 이력이 없습니다."}
            </p>
          ) : (
            items.map((t) =>
              editingId === t.thread_id ? (
                <div
                  key={t.thread_id}
                  className="flex items-center gap-1 rounded-lg bg-muted px-2 py-2"
                >
                  <MessageSquareText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <input
                    autoFocus
                    value={editTitle}
                    maxLength={200}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => onEditKey(e, t.thread_id)}
                    placeholder="제목 입력(비우면 원래 제목)"
                    className="min-w-0 flex-1 rounded border border-border bg-background px-1.5 py-1 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <button
                    onClick={() => saveEdit(t.thread_id)}
                    disabled={saving}
                    className="shrink-0 rounded p-1 text-primary hover:bg-background disabled:opacity-50"
                    aria-label="저장"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="shrink-0 rounded p-1 text-muted-foreground hover:bg-background"
                    aria-label="취소"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  key={t.thread_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    onSelect(t.thread_id);
                    onClose();
                  }}
                  className={cn(
                    "group flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left hover:bg-muted",
                    t.thread_id === currentThreadId && "bg-muted",
                  )}
                >
                  <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{t.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t.last_at?.slice(0, 10)} · {t.count}문답
                    </span>
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => startEdit(e, t.thread_id, t.title)}
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100"
                    aria-label="제목 수정"
                  >
                    <Pencil className="h-4 w-4" />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => onDelete(e, t.thread_id)}
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                    aria-label="삭제"
                  >
                    <Trash2 className="h-4 w-4" />
                  </span>
                </div>
              ),
            )
          )}
        </div>
      </aside>
    </>
  );
}
